import { create } from "zustand";
import { settings } from "@/shared";
import api from "@/shared/utils/api";
import { getCookie, setCookie, removeCookie } from "@/shared/utils/cookies";
import { User, AuthResponse } from "@/shared/types";
import { AxiosRequestConfig } from "axios";

// Расширяем тип AxiosRequestConfig
interface CustomAxiosConfig extends AxiosRequestConfig {
  skipAuthRetry?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<string>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post(settings.api.auth.login, {
        email,
        password,
      });

      if (response.data.role !== "super_admin") {
        throw new Error("You are not authorized to access this application");
      }

      if (response.data) {
        const { access_token, refresh_token, ...userData } = response.data;

        // Store tokens
        setCookie(settings.cookies.access_token, access_token);
        setCookie(settings.cookies.refresh_token, refresh_token);

        // Store user data
        set({
          user: userData,
          isAuthenticated: true,
          error: null,
        });

        setCookie(settings.cookies.user, JSON.stringify(userData));

        if (userData.organization) {
          // Make sure we save all organization fields, including created_at
          setCookie(
            settings.cookies.organization,
            JSON.stringify(userData.organization)
          );
        }

        return response.data;
      }
      throw new Error("Invalid response from server");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred during login";
      set({ error: errorMessage, isAuthenticated: false, user: null });
      console.error("Login error:", err);
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true });
      
      // Проверяем, есть ли токен доступа перед вызовом API
      const accessToken = getCookie(settings.cookies.access_token);
      if (accessToken) {
        // Вызываем API только если есть токен
        try {
          await api.post(settings.api.auth.logout, {}, { skipAuthRetry: true } as CustomAxiosConfig);
        } catch (err) {
          console.warn("Failed to call logout API:", err);
          // Игнорируем ошибки в случае сбоя logout API
        }
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        error: null,
        isLoading: false,
      });

      // Clear all auth-related cookies
      removeCookie(settings.cookies.access_token);
      removeCookie(settings.cookies.refresh_token);
      removeCookie(settings.cookies.user);
      removeCookie(settings.cookies.organization);
      
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
  },

  refreshToken: async () => {
    try {
      const refresh_token = getCookie(settings.cookies.refresh_token);
      if (!refresh_token) {
        throw new Error("No refresh token available");
      }

      const response = await api.post(settings.api.auth.refresh, {
        refresh_token,
      });

      if (response.data?.access_token) {
        setCookie(settings.cookies.access_token, response.data.access_token);
        return response.data.access_token;
      }

      throw new Error("Failed to refresh token");
    } catch (error) {
      console.error("Token refresh error:", error);
      // Вместо вызова logout просто очищаем куки и устанавливаем состояние
      set({
        user: null,
        isAuthenticated: false,
        error: error instanceof Error ? error.message : "Authentication failed",
      });
      
      removeCookie(settings.cookies.access_token);
      removeCookie(settings.cookies.refresh_token);
      removeCookie(settings.cookies.user);
      removeCookie(settings.cookies.organization);
      
      throw error;
    }
  },

  checkAuth: async () => {
    try {
      // Если мы уже на странице логина, не делаем проверку авторизации
      if (window.location.pathname === "/login") {
        set({ isLoading: false });
        return;
      }
      
      // Проверяем наличие токенов заранее
      const accessToken = getCookie(settings.cookies.access_token);
      const refreshToken = getCookie(settings.cookies.refresh_token);
      
      // Если токенов нет, просто устанавливаем состояние не авторизован
      if (!accessToken && !refreshToken) {
        set({ 
          isAuthenticated: false, 
          user: null,
          isLoading: false 
        });
        return;
      }
      
      set({ isLoading: true });
      
      // Пытаемся получить информацию о пользователе
      const response = await api.get(settings.api.auth.check);

      if (response.data) {
        set({
          user: response.data,
          isAuthenticated: true,
          error: null,
        });

        setCookie(settings.cookies.user, JSON.stringify(response.data));
        if (response.data.organization) {
          setCookie(
            settings.cookies.organization,
            JSON.stringify(response.data.organization)
          );
        }
      }
    } catch (error) {
      console.error("Auth check error:", error);
      
      // Тут мы просто устанавливаем состояние не авторизован, не вызывая logout
      set({
        isAuthenticated: false,
        user: null,
        error: error instanceof Error ? error.message : "Authentication failed",
      });
      
      // Очищаем куки, но не делаем редирект (он произойдет через компонент PrivateRoute)
      removeCookie(settings.cookies.access_token);
      removeCookie(settings.cookies.refresh_token);
      removeCookie(settings.cookies.user);
      removeCookie(settings.cookies.organization);
    } finally {
      set({ isLoading: false });
    }
  },
}));
